import React, { useContext, useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { TicketContext } from '../../../app/providers/TicketContext';
import { FiSearch, FiDownload, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight, FiChevronDown } from 'react-icons/fi';
import * as XLSX from 'xlsx';

const EventTable = ({
  tableContent,
  tableHeader,
  tableColumnKey,
  showSelectButton,
  eventFNAME,
  selectType = 'button',       // 'button' | 'checkbox'
  onCheckboxSelectionChange,   // (values: string[], rawRows: object[]) => void
}) => {
  const { SendEvent } = useContext(TicketContext);
  
  // showSelectButton için: eğer hiç değer gelmiyorsa (undefined, null, vs.) false olarak değerlendir
  const shouldShowSelectButton = showSelectButton === true;
  const isCheckboxMode = selectType === 'checkbox' && shouldShowSelectButton;

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isTableDisabled, setIsTableDisabled] = useState(false);
  // Checkbox mode: tüm sorted data üzerinden seçim (sayfa bağımsız)
  const [selectedRowKeys, setSelectedRowKeys] = useState(new Set()); // key = JSON.stringify(row)
  const exportMenuRef = useRef(null);

  // Sütun genişlikleri — sürükleyerek yeniden boyutlandırılabilir
  const DEFAULT_COL_WIDTH = 120;
  const MIN_COL_WIDTH = 48;
  const [columnWidths, setColumnWidths] = useState({});
  const resizingRef = useRef(null); // { colKey, startX, startWidth }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExportMenu]);

  // normalizedHeader değiştiğinde sütun genişliklerini başlat (mevcut genişlikleri koru)
  useEffect(() => {
    setColumnWidths(prev => {
      const next = { ...prev };
      if (shouldShowSelectButton && !next['__select__']) {
        next['__select__'] = 64;
      }
      return next;
    });
  }, [shouldShowSelectButton]);

  const getColWidth = useCallback((key) => {
    return columnWidths[key] ?? DEFAULT_COL_WIDTH;
  }, [columnWidths]);

  const handleResizeMouseDown = useCallback((e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    const startWidth = columnWidths[colKey] ?? DEFAULT_COL_WIDTH;
    resizingRef.current = { colKey, startX: e.clientX, startWidth };

    const onMouseMove = (me) => {
      if (!resizingRef.current) return;
      const { colKey: ck, startX, startWidth: sw } = resizingRef.current;
      const delta = me.clientX - startX;
      const newWidth = Math.max(MIN_COL_WIDTH, sw + delta);
      setColumnWidths(prev => ({ ...prev, [ck]: newWidth }));
    };

    const onMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [columnWidths]);

  const normalizedHeader = useMemo(() => {
    if (Array.isArray(tableHeader) && tableHeader.length > 0) {
      return tableHeader;
    }

    if (Array.isArray(tableContent) && tableContent.length > 0) {
      const firstRow = tableContent[0];
      return Object.keys(firstRow || {}).map(key => ({
        FIELD: key,
        DESC: key
      }));
    }

    return [];
  }, [tableHeader, tableContent]);

  const primaryColumnKey = useMemo(() => {
    // Eğer tableColumnKey bir string ise, direkt döndür
    if (typeof tableColumnKey === 'string') {
      return tableColumnKey;
    }

    if (tableColumnKey && typeof tableColumnKey === 'object') {
      return null; // Object durumunda özel işlem yapılacak
    }

    // Fallback: tableColumnKey yoksa veya geçersizse
    if (normalizedHeader.length > 0) {
      return normalizedHeader[0].FIELD;
    }

    if (Array.isArray(tableContent) && tableContent.length > 0) {
      return Object.keys(tableContent[0] || {})[0];
    }

    return null;
  }, [tableColumnKey, normalizedHeader, tableContent]);

  // Bir row'dan tableColumnKey'e göre value string'i üretir
  const extractRowValue = (row) => {
    if (tableColumnKey && typeof tableColumnKey === 'object' && !Array.isArray(tableColumnKey)) {
      const parts = Object.entries(tableColumnKey)
        .map(([key, fieldName]) => row[fieldName] || row[key] || '')
        .filter(v => v !== '' && v !== null && v !== undefined);
      return parts.join(' ');
    }
    if (tableColumnKey && typeof tableColumnKey === 'string') {
      return String(row[tableColumnKey] ?? '');
    }
    return primaryColumnKey ? String(row[primaryColumnKey] ?? '') : JSON.stringify(row);
  };

  const handleSelect = (row) => {
    if (isTableDisabled) return;
    
    if (!eventFNAME) {
      console.warn("eventFNAME bulunamadı - EventTable'ın kendi event'inin FNAME'i gerekli", { eventFNAME });
      return;
    }

    const message = extractRowValue(row);
    const rowJSON = JSON.stringify(row);
    const eventData = {
      inputName: eventFNAME,
      message: message,
      data: rowJSON
    };
    SendEvent(eventData, [], 'EventTable-SelectButton');
    setIsTableDisabled(true);
  };

  const filteredData = useMemo(() => {
    if (!searchTerm) return tableContent || [];
    
    return (tableContent || []).filter(row => 
      Object.values(row).some(value => 
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [tableContent, searchTerm]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = sortedData.slice(startIndex, endIndex);

  // Checkbox mode handlers — currentData ve sortedData tanımlandıktan SONRA gelir
  const rowKey = (row) => JSON.stringify(row);

  const handleCheckboxToggle = (row) => {
    if (isTableDisabled) return;
    const k = rowKey(row);
    setSelectedRowKeys(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      const selectedRows = sortedData.filter(r => next.has(rowKey(r)));
      const values = selectedRows.map(r => extractRowValue(r));
      onCheckboxSelectionChange?.(values, selectedRows);
      return next;
    });
  };

  const isAllPageSelected = currentData.length > 0 && currentData.every(r => selectedRowKeys.has(rowKey(r)));
  const isSomePageSelected = !isAllPageSelected && currentData.some(r => selectedRowKeys.has(rowKey(r)));

  const handleSelectAllPage = () => {
    if (isTableDisabled) return;
    setSelectedRowKeys(prev => {
      const next = new Set(prev);
      if (isAllPageSelected) {
        currentData.forEach(r => next.delete(rowKey(r)));
      } else {
        currentData.forEach(r => next.add(rowKey(r)));
      }
      const selectedRows = sortedData.filter(r => next.has(rowKey(r)));
      const values = selectedRows.map(r => extractRowValue(r));
      onCheckboxSelectionChange?.(values, selectedRows);
      return next;
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const exportToCSV = () => {
    const headers = normalizedHeader.map(h => h.DESC || h.FIELD);
    const csvContent = [
      headers.join(','),
      ...sortedData.map(row => 
        normalizedHeader.map(h => {
          const value = row[h.FIELD] || '';
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `table_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportMenu(false);
  };

  const exportToXLSX = () => {
    const headers = normalizedHeader.map(h => h.DESC || h.FIELD);
    const data = sortedData.map(row => 
      normalizedHeader.map(h => row[h.FIELD] || '')
    );

    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    
    const colWidths = headers.map((header, i) => {
      const maxLength = Math.max(
        header.length,
        ...data.map(row => String(row[i] || '').length)
      );
      return { wch: Math.min(maxLength + 2, 50) };
    });
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Table Data');
    
    XLSX.writeFile(wb, `table_export_${new Date().toISOString().split('T')[0]}.xlsx`);
    setShowExportMenu(false);
  };

  if (!tableContent || normalizedHeader.length === 0) {
    return <div>No data available</div>;
  }

  return (
    <div className={`w-full max-w-full bg-white border border-[#c0c0c0] shadow-lg overflow-hidden text-[11px] ${isTableDisabled ? 'opacity-70' : ''}`}>
      <div className="bg-[#f8f9fa] px-3 py-2.5 border-b border-[#c0c0c0] shadow-sm">
        <div className="flex flex-col sm:flex-row gap-2 items-center justify-between">
          <div className="relative" ref={exportMenuRef}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="inline-flex items-center px-3 py-1.5 border border-[#8a8886] text-[10px] font-medium rounded-sm text-[#323130] bg-white hover:bg-[#f3f2f1] active:bg-[#edebe9] focus:outline-none focus:ring-2 focus:ring-[#0078d4] focus:ring-offset-1 transition-colors duration-100 shadow-sm"
              disabled={isTableDisabled}
            >
              <FiDownload className="w-3.5 h-3.5 mr-1.5" />
              <span className="font-semibold">Dışa Aktar</span>
              <FiChevronDown className={`w-3 h-3 ml-1.5 transition-transform duration-200 ${showExportMenu ? 'rotate-180' : ''}`} />
            </button>

            {showExportMenu && (
              <div className="absolute left-0 mt-1 w-48 rounded-sm shadow-xl bg-white border border-[#c0c0c0] z-50 animate-fadeIn text-[11px]">
                <div className="py-1" role="menu">
                  <button
                    onClick={exportToXLSX}
                    className="w-full text-left px-3 py-2 text-[#323130] hover:bg-[#e8f4f8] active:bg-[#d4e8f4] flex items-center transition-colors duration-100 border-b border-[#e1dfdd] last:border-b-0"
                    role="menuitem"
                  >
                    <svg className="w-4 h-4 mr-3 text-[#107c10] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 2a2 2 0 00-2 2v8a2 2 0 002 2h6a2 2 0 002-2V6.414A2 2 0 0016.414 5L14 2.586A2 2 0 0012.586 2H9z"/>
                      <path d="M3 8a2 2 0 012-2v10h8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"/>
                    </svg>
                    <div>
                      <div className="font-semibold text-[#323130]">Excel (XLSX)</div>
                      <div className="text-[10px] text-[#605e5c]">Microsoft Excel format</div>
                    </div>
                  </button>
                  <button
                    onClick={exportToCSV}
                    className="w-full text-left px-3 py-2 text-[#323130] hover:bg-[#e8f4f8] active:bg-[#d4e8f4] flex items-center transition-colors duration-100"
                    role="menuitem"
                  >
                    <svg className="w-4 h-4 mr-3 text-[#0078d4] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd"/>
                    </svg>
                    <div>
                      <div className="font-semibold text-[#323130]">CSV</div>
                      <div className="text-[10px] text-[#605e5c]">Comma separated values</div>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
              <FiSearch className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-8 pr-3 py-1.5 border border-[#8a8886] rounded-sm leading-5 bg-white placeholder-[#8a8886] focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4] focus:ring-offset-0 text-xs text-[#323130] shadow-sm"
              disabled={isTableDisabled}
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto bg-white">
        <table
          className="border-collapse text-[11px] bg-white"
          style={{
            fontFamily: 'Calibri, Arial, sans-serif',
            tableLayout: 'fixed',
            width: 'max-content',
            minWidth: '100%',
          }}
        >
          <colgroup>
            {shouldShowSelectButton && (
              <col style={{ width: getColWidth('__select__') }} />
            )}
            {normalizedHeader.map((header, index) => (
              <col key={index} style={{ width: getColWidth(header.FIELD + '_' + index) }} />
            ))}
          </colgroup>
          <thead>
            <tr className="bg-[#e7e6e6] border-b-2 border-[#b4b4b4]">
              {shouldShowSelectButton && (
                <th
                  className="px-1.5 py-0.5 text-center text-[#323130] font-semibold border-r border-[#b4b4b4] bg-[#e7e6e6] uppercase tracking-wide text-[10px] sticky left-0 z-10 shadow-[2px_0_2px_-1px_rgba(0,0,0,0.1)]"
                  style={{ position: 'relative', overflow: 'hidden' }}
                >
                  {isCheckboxMode ? (
                    <div className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={isAllPageSelected}
                        ref={el => { if (el) el.indeterminate = isSomePageSelected; }}
                        onChange={handleSelectAllPage}
                        disabled={isTableDisabled}
                        className="h-3.5 w-3.5 cursor-pointer accent-[#0078d4]"
                        title="Bu sayfadakileri tümünü seç/kaldır"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center leading-tight overflow-hidden">
                      <span className="truncate">{isTableDisabled ? 'Seçildi' : 'Seçim'}</span>
                    </div>
                  )}
                  {/* Resize handle */}
                  <div
                    onMouseDown={(e) => handleResizeMouseDown(e, '__select__')}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute', right: 0, top: 0, bottom: 0, width: 5,
                      cursor: 'col-resize', zIndex: 2,
                      background: 'transparent',
                    }}
                    className="hover:bg-[#0078d4]/40 transition-colors"
                    title="Sütun genişliğini ayarla"
                  />
                </th>
              )}
              {normalizedHeader.map((header, index) => {
                const colKey = header.FIELD + '_' + index;
                return (
                  <th
                    key={index}
                    className={`px-1.5 py-0.5 text-center text-[#323130] font-semibold border-r border-[#b4b4b4] bg-[#e7e6e6] uppercase tracking-wide text-[10px] transition-colors duration-100 ${
                      isTableDisabled
                        ? 'cursor-not-allowed opacity-70'
                        : 'cursor-pointer hover:bg-[#d4d4d4] active:bg-[#c8c8c8]'
                    }`}
                    onClick={() => !isTableDisabled && handleSort(header.FIELD)}
                    style={{
                      position: 'relative',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      lineHeight: '1.2',
                      overflow: 'hidden',
                    }}
                  >
                    <div className="flex items-center justify-center gap-1 leading-tight overflow-hidden">
                      <span
                        className="truncate"
                        title={header.DESC || `Kolon ${index + 1}`}
                      >
                        {header.DESC || `Kolon ${index + 1}`}
                      </span>
                      {sortConfig.key === header.FIELD && (
                        <span className="text-[#0078d4] text-xs font-bold flex-shrink-0">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </div>
                    {/* Resize handle */}
                    <div
                      onMouseDown={(e) => handleResizeMouseDown(e, colKey)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: 'absolute', right: 0, top: 0, bottom: 0, width: 5,
                        cursor: 'col-resize', zIndex: 2,
                        background: 'transparent',
                      }}
                      className="hover:bg-[#0078d4]/40 transition-colors"
                      title="Sütun genişliğini ayarla"
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-white text-[11px]">
            {currentData.map((row, rowIndex) => {
              const isEvenRow = rowIndex % 2 === 0;
              return (
                <tr
                  key={rowIndex}
                  className={`border-b border-[#d0d0d0] ${
                    isTableDisabled
                      ? 'opacity-60'
                      : isEvenRow
                        ? 'bg-white hover:bg-[#e8f4f8]'
                        : 'bg-[#fafafa] hover:bg-[#e8f4f8]'
                  }`}
                >
                  {shouldShowSelectButton && (
                    <td className="px-1.5 py-0.5 border-r border-[#d0d0d0] bg-inherit sticky left-0 z-10 shadow-[2px_0_2px_-1px_rgba(0,0,0,0.05)] align-middle text-center" style={{ overflow: 'hidden' }}>
                      <div className="flex items-center justify-center">
                        {isCheckboxMode ? (
                          <input
                            type="checkbox"
                            checked={selectedRowKeys.has(rowKey(row))}
                            onChange={() => handleCheckboxToggle(row)}
                            disabled={isTableDisabled}
                            className="h-3.5 w-3.5 cursor-pointer accent-[#0078d4]"
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleSelect(row)}
                            disabled={isTableDisabled}
                            className={`inline-flex items-center px-1.5 py-0.5 border text-[10px] font-medium rounded-sm transition-colors duration-100 leading-tight ${
                              isTableDisabled
                                ? 'cursor-not-allowed border-transparent bg-gray-300 text-gray-500'
                                : 'border-[#0078d4] text-[#0078d4] bg-white hover:bg-[#0078d4] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#0078d4] focus:ring-offset-1 shadow-sm'
                            }`}
                          >
                            <svg className="w-2 h-2 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {isTableDisabled ? 'Seçildi' : 'Seç'}
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                  {normalizedHeader.map((header, colIndex) => {
                    const cellValue = row[header.FIELD];
                    const rawText = cellValue !== undefined && cellValue !== null && cellValue !== ''
                      ? String(cellValue)
                      : '';
                    const displayValue = rawText || '\u00A0';

                    return (
                      <td
                        key={colIndex}
                        className="px-1.5 py-0.5 text-[#323130] border-r border-[#d0d0d0] bg-inherit align-middle"
                        style={{
                          overflow: 'hidden',
                          lineHeight: '1.4',
                          maxWidth: 0,
                        }}
                        title={rawText}
                      >
                        <div
                          className="text-[#323130] w-full leading-tight"
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {displayValue}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {/* Sabit yükseklik için boş placeholder satırlar — arama sırasında yükseklik korunur */}
            {/* Checkbox mode: seçili satır özeti */}
            {isCheckboxMode && selectedRowKeys.size > 0 && (
              <tr className="bg-[#e8f4f8] border-b-2 border-[#0078d4]">
                <td
                  colSpan={normalizedHeader.length + 1}
                  className="px-2 py-1 text-[10px] font-semibold text-[#0078d4]"
                >
                  {selectedRowKeys.size} satır seçildi
                </td>
              </tr>
            )}
            {Array.from({ length: pageSize - currentData.length }).map((_, i) => (
              <tr key={`placeholder-${i}`} className="border-b border-[#d0d0d0]" aria-hidden="true">
                {shouldShowSelectButton && (
                  <td className="px-1.5 py-0.5 border-r border-[#d0d0d0]" style={{ overflow: 'hidden' }}>&nbsp;</td>
                )}
                {normalizedHeader.map((_, colIndex) => (
                  <td key={colIndex} className="px-1.5 py-0.5 border-r border-[#d0d0d0]" style={{ overflow: 'hidden', maxWidth: 0 }}>&nbsp;</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="bg-[#f8f9fa] px-3 py-2.5 border-t border-[#c0c0c0] shadow-inner">
        <div className="flex items-center justify-between text-[10.5px]">
          <div className="flex items-center space-x-1.5">
            <span className="text-[#323130] font-medium">Sayfa boyutu:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border border-[#8a8886] rounded-sm px-2 py-1 text-[10.5px] bg-white text-[#323130] focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4] focus:ring-offset-0 shadow-sm"
              disabled={isTableDisabled}
            >
              {[10, 20, 50].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-1 text-[10px]">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1 || isTableDisabled}
              className="p-1.5 rounded-sm border border-[#8a8886] bg-white text-[#323130] disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-[#f3f2f1] hover:bg-[#f3f2f1] active:bg-[#edebe9] focus:outline-none focus:ring-2 focus:ring-[#0078d4] focus:ring-offset-1 transition-colors duration-100 shadow-sm"
            >
              <FiChevronsLeft className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || isTableDisabled}
              className="p-1.5 rounded-sm border border-[#8a8886] bg-white text-[#323130] disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-[#f3f2f1] hover:bg-[#f3f2f1] active:bg-[#edebe9] focus:outline-none focus:ring-2 focus:ring-[#0078d4] focus:ring-offset-1 transition-colors duration-100 shadow-sm"
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            
            <span className="text-[#323130] px-2 py-1 font-semibold bg-white border border-[#8a8886] rounded-sm shadow-sm">
              {totalPages > 0 ? `Sayfa ${currentPage} / ${totalPages}` : '0 kayıt'}
            </span>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0 || isTableDisabled}
              className="p-1.5 rounded-sm border border-[#8a8886] bg-white text-[#323130] disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-[#f3f2f1] hover:bg-[#f3f2f1] active:bg-[#edebe9] focus:outline-none focus:ring-2 focus:ring-[#0078d4] focus:ring-offset-1 transition-colors duration-100 shadow-sm"
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0 || isTableDisabled}
              className="p-1.5 rounded-sm border border-[#8a8886] bg-white text-[#323130] disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-[#f3f2f1] hover:bg-[#f3f2f1] active:bg-[#edebe9] focus:outline-none focus:ring-2 focus:ring-[#0078d4] focus:ring-offset-1 transition-colors duration-100 shadow-sm"
            >
              <FiChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Tablo alt bilgi - Excel benzeri status bar */}
      <div className="bg-[#e7e6e6] px-3 py-2 border-t border-[#b4b4b4] shadow-inner">
        <div className="flex items-center justify-between text-[10.5px]">
          <div className="text-[#323130] font-medium">
            <span className="font-semibold">{filteredData.length}</span> kayıt bulundu
            {searchTerm && (
              <span className="ml-2 text-[#0078d4] font-semibold">
                (Arama: "{searchTerm}")
              </span>
            )}
          </div>
          <div className="text-[#605e5c] text-[10px]">
            {new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventTable;
